import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import campaignValues from '../../../utils/campaignValues.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import NumberRounding from '../../../utils/numberRounding.js';
import { stateService } from '../../../utils/stateService.js';
import OrderService from '../../../services/orderService.js';
import QueryStringService from '../../../services/queryStringService.js';
import { useFetchCampaignData } from '../../../hooks/campaign/useFetchCampaignData.js';
import styles from './CampaignTable.module.css';
import { CAMPAIGN_STATUS, CAMPAING_PROMISE_TYPES } from '../../../constants/enums.js';
import { usePromiseConfigForLp5 } from '../Contexts/Lp5ConfigurationContext';

const loadingSrc = require('../../../images/loading-black.svg');

const hasAirportPlaysPromise = (localParameter5) =>
    typeof localParameter5 === 'string' &&
    localParameter5.toLowerCase() === CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1;

const TableRow = React.memo(({
    className,
    customerId,
    orderline,
    onSelectedOrderlineId,
    customerName,
    countryCode,
    onDoubleClickOrderlineId,
    showProductNameColumn
}) => {
    const promiseConfig = usePromiseConfigForLp5(orderline.localParameter5);
    const calculatedCampaignStatus = useMemo(() =>
        campaignValues.campaignStatus(orderline.periodStart, orderline.periodEnd),
        [orderline.periodStart, orderline.periodEnd]
    );

    const { loading, data, error } = useFetchCampaignData(
        orderline ?? null,
        customerId ?? null,
        countryCode ?? null
    );

    const calculatedDelivery = useMemo(() => {
        if (!data) return 0;

        return NumberRounding.round(
            campaignValues.calculateDeliveryPercentageBasedOnPromise(
                [orderline],
                data,
                promiseConfig
            ) * 100,
            0
        );
    }, [data, orderline, promiseConfig]);

    const handleRowClick = useCallback(
        () => onSelectedOrderlineId(orderline.id),
        [onSelectedOrderlineId, orderline.id]
    );

    const handleRowDoubleClick = useCallback(
        () => onDoubleClickOrderlineId(orderline.id),
        [onDoubleClickOrderlineId, orderline.id]
    );

    const campaignName = useMemo(() => 
        campaignValues.caluclateCampaignName
            (orderline.alternativeName, customerName, orderline.periodStart, orderline.periodEnd),
        [orderline.alternativeName, customerName, orderline.periodStart, orderline.periodEnd]
    );

    return (
        <tr
            className={className}
            onClick={handleRowClick}
            onDoubleClick={handleRowDoubleClick}
        >
            <td className={styles.campaignDetails}>{campaignName}</td>
            <td className={styles.campaignDetails}>
                {campaignValues.productTypeDescription(countryCode, orderline.type)}
            </td>
            <td className={styles.campaignDetails}>{orderline.product}</td>
            {showProductNameColumn && (
                <td className={styles.campaignDetails}>{orderline.name}</td>
            )}
            <td className={styles.campaignDetails}>
                w.{dateFunctions.calculateCampaignWeeks(
                    orderline.periodStart,
                    orderline.periodEnd
                )}
            </td>
            <td className={styles.campaignDetails}>
                {calculatedCampaignStatus === CAMPAIGN_STATUS.NOT_STARTED ? "Not started" :
                    calculatedCampaignStatus === CAMPAIGN_STATUS.COMPLETED ? "Completed" :
                        "Ongoing"
                }
            </td>
            <td className={styles.campaignDetails}>
                {loading
                    ? <img src={loadingSrc} height="20" alt="Loading" />
                    : error
                        ? <span title={error.message}>Error</span>
                        : <span>{calculatedDelivery}%</span>
                }
            </td>
        </tr>
    );
});

TableRow.propTypes = {
    className: PropTypes.string,
    customerId: PropTypes.string.isRequired,
    orderline: PropTypes.object.isRequired,
    onSelectedOrderlineId: PropTypes.func.isRequired,
    customerName: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    onDoubleClickOrderlineId: PropTypes.func.isRequired,
    showProductNameColumn: PropTypes.bool.isRequired
};

const CampaignTable = ({ customerId, orderData, selectedOrderlineId, onTableRowClick, onTableRowDoubleClick }) => {
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const [localSelectedOrderlineId, setLocalSelectedOrderlineId] = useState(selectedOrderlineId);
    const [countryCode,] = useState(queryStringService.getCurrentCountry());

    useEffect(() => {
        setLocalSelectedOrderlineId(selectedOrderlineId);
    }, [selectedOrderlineId]);

    const handleTableRowClick = useCallback((orderlineId) => {
        onTableRowClick(orderlineId);
        setLocalSelectedOrderlineId(orderlineId);
    }, [onTableRowClick]);

    const handleTableRowDoubleClick = useCallback((orderlineId) => {
        onTableRowDoubleClick(orderlineId);
    }, [onTableRowDoubleClick]);

    const showProductNameColumn = useMemo(() =>
        orderData.orderLines.some(campaign => hasAirportPlaysPromise(campaign.localParameter5)),
        [orderData.orderLines]
    );
    
    const tableHeaders = useMemo(() => {
        const headers = ["Campaign", "Product Type", "Product"];

        if (showProductNameColumn) {
            headers.push("Product Name");
        }

        headers.push("Period", "Campaign status", "Delivery %");
        return headers;
    }, [showProductNameColumn]);

    return (
        <div className={styles.tableContainer}>
            <table className={styles.campaignTable}>
                <thead>
                    <tr>
                        {tableHeaders.map((header, index) => (
                            <th key={index} className={styles.campaignHeader}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {orderData.orderLines.map((campaign) => (
                        <TableRow
                            key={campaign.id}
                            customerId={customerId}
                            className={localSelectedOrderlineId === campaign.id ? styles.selectedCampaign : styles.campaignRow}
                            orderline={campaign}
                            onSelectedOrderlineId={handleTableRowClick}
                            onDoubleClickOrderlineId={handleTableRowDoubleClick}
                            customerName={orderData.customer}
                            countryCode={countryCode}
                            showProductNameColumn={showProductNameColumn}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

CampaignTable.propTypes = {
    customerId: PropTypes.string.isRequired,
    orderData: PropTypes.shape({
        customer: PropTypes.string.isRequired,
        orderLines: PropTypes.array.isRequired
    }).isRequired,
    selectedOrderlineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onTableRowClick: PropTypes.func.isRequired,
    onTableRowDoubleClick: PropTypes.func.isRequired
};

export default React.memo(CampaignTable);